import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Package, Search, AlertTriangle, ArrowLeft, Plus, Minus, 
  TrendingDown, History, DollarSign, RefreshCw 
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getBranchById } from '../../api/branches';
import { 
  getBranchInventory, 
  getLowStockItems, 
  getBranchInventoryValue,
  adjustStock 
} from '../../api/branchInventory';
import { useAuth } from '../../contexts/AuthContext';
import AddProductToBranchModal from '../../components/branches/AddProductToBranchModal';

const BranchInventoryPage = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'retail_admin', 'branch_admin', 'system_admin'].includes(user?.role);

  const [branch, setBranch] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [inventoryValue, setInventoryValue] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Adjustment modal state
  const [adjustmentModal, setAdjustmentModal] = useState({
    isOpen: false,
    product: null,
    type: 'add', // 'add' or 'remove'
    quantity: '',
    reason: ''
  });

  // Add products modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Fetch branch details
  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const data = await getBranchById(branchId);
        setBranch(data);
      } catch (error) {
        toast.error('Failed to load branch');
      }
    };
    fetchBranch();
  }, [branchId]);

  // Fetch inventory
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const [inventoryData, valueData] = await Promise.all([
        getBranchInventory(branchId, {
          search: searchTerm,
          lowStock: showLowStockOnly,
          limit: pagination.limit,
          offset: (pagination.page - 1) * pagination.limit
        }),
        getBranchInventoryValue(branchId)
      ]);

      setInventory(inventoryData.inventory || []);
      setPagination(prev => ({
        ...prev,
        total: inventoryData.total,
        totalPages: inventoryData.pagination?.totalPages || 1
      }));
      setInventoryValue(valueData);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [branchId, searchTerm, showLowStockOnly, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Handle stock adjustment
  const handleAdjust = async () => {
    const { product, type, quantity, reason } = adjustmentModal;
    const adjustmentValue = type === 'add' ? parseInt(quantity) : -parseInt(quantity);

    if (!quantity || isNaN(adjustmentValue) || adjustmentValue === 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await adjustStock(branchId, product.product_id, adjustmentValue, reason || `Stock ${type}`);
      toast.success(`Stock ${type === 'add' ? 'added' : 'removed'} successfully`);
      setAdjustmentModal({ isOpen: false, product: null, type: 'add', quantity: '', reason: '' });
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to adjust stock');
    }
  };

  const openAdjustmentModal = (product, type) => {
    setAdjustmentModal({
      isOpen: true,
      product,
      type,
      quantity: '',
      reason: ''
    });
  };

  const getStockStatusColor = (item) => {
    if (item.stock_quantity <= 0) return 'text-red-600 bg-red-50';
    if (item.is_low_stock) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getStockStatusLabel = (item) => {
    if (item.stock_quantity <= 0) return 'Out of Stock';
    if (item.is_low_stock) return 'Low Stock';
    return 'In Stock';
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/branches/${branchId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  {branch?.name || 'Branch'} Inventory
                </h1>
                <p className="text-xs sm:text-sm text-secondary-gray">
                  Manage stock levels for this branch
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {isAdmin && (
                <button
                  onClick={() => setShowAddProductModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Products
                </button>
              )}
              <button
                onClick={() => navigate(`/stock-transfers?fromBranch=${branchId}&create=true`)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600"
              >
                <RefreshCw className="w-4 h-4" />
                Create Transfer
              </button>
            </div>
          </div>
        </div>

        {/* Value Summary Cards */}
        {inventoryValue && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 text-secondary-gray mb-1">
                <Package className="w-4 h-4" />
                <span className="text-xs">Products</span>
              </div>
              <p className="text-xl font-bold">{inventoryValue.product_count || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 text-secondary-gray mb-1">
                <TrendingDown className="w-4 h-4" />
                <span className="text-xs">Total Units</span>
              </div>
              <p className="text-xl font-bold">{parseInt(inventoryValue.total_units || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 text-secondary-gray mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs">Cost Value</span>
              </div>
              <p className="text-xl font-bold text-blue-600">
                ₦{parseFloat(inventoryValue.total_cost_value || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 text-secondary-gray mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs">Retail Value</span>
              </div>
              <p className="text-xl font-bold text-green-600">
                ₦{parseFloat(inventoryValue.total_retail_value || 0).toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showLowStockOnly}
                onChange={(e) => setShowLowStockOnly(e.target.checked)}
                className="w-4 h-4 text-accent-gold rounded"
              />
              <span className="text-sm flex items-center gap-1">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Low Stock Only
              </span>
            </label>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-accent-gold border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-secondary-gray">Loading inventory...</p>
            </div>
          ) : inventory.length === 0 ? (
            <div className="p-8 text-center text-secondary-gray">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No inventory items found</p>
              <p className="text-sm mt-2">Assign products to this branch to manage inventory</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-primary-black text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Product</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Stock</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Reorder Level</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Value</th>
                      {isAdmin && <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {inventory.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.product_code}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-lg">{item.stock_quantity}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-500">
                          {item.reorder_level}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStockStatusColor(item)}`}>
                            {getStockStatusLabel(item)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-medium">₦{parseFloat(item.stock_value || 0).toLocaleString()}</p>
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openAdjustmentModal(item, 'add')}
                                className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                                title="Add Stock"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openAdjustmentModal(item, 'remove')}
                                className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                                title="Remove Stock"
                                disabled={item.stock_quantity <= 0}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => navigate(`/branches/${branchId}/inventory/${item.product_id}/history`)}
                                className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                                title="View History"
                              >
                                <History className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="border-t px-4 py-3 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stock Adjustment Modal */}
      {adjustmentModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">
              {adjustmentModal.type === 'add' ? 'Add' : 'Remove'} Stock
            </h2>
            <p className="text-gray-600 mb-4">
              {adjustmentModal.product?.product_name}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Current stock: <strong>{adjustmentModal.product?.stock_quantity}</strong>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={adjustmentModal.quantity}
                  onChange={(e) => setAdjustmentModal(prev => ({ ...prev, quantity: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                  placeholder="Enter quantity"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Reason (Optional)</label>
                <input
                  type="text"
                  value={adjustmentModal.reason}
                  onChange={(e) => setAdjustmentModal(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                  placeholder="e.g., Stock take adjustment"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setAdjustmentModal({ isOpen: false, product: null, type: 'add', quantity: '', reason: '' })}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjust}
                className={`flex-1 px-4 py-2 rounded-lg text-white ${
                  adjustmentModal.type === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {adjustmentModal.type === 'add' ? 'Add Stock' : 'Remove Stock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Products Modal */}
      {showAddProductModal && (
        <AddProductToBranchModal
          branchId={branchId}
          branchName={branch?.name}
          onClose={() => setShowAddProductModal(false)}
          onSuccess={(count) => {
            toast.success(`${count} product(s) added to branch inventory`);
            fetchInventory();
          }}
        />
      )}
    </div>
  );
};

export default BranchInventoryPage;
